import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { PrefetchPageLinks, useLocation, useNavigate } from '@remix-run/react';
import { RenderHostContext, type RenderHost } from '~/utils/live-host/render-host';
import type { VisitorAudience } from '~/utils/live-host/conditions';
import { PAGE_RUNTIME_JS } from '~/utils/live-host/page-runtime.generated';
import { installPageRuntime, type PageLiveData } from '~/utils/live-host/page-runtime';

/**
 * The host side of live states for a builder page — MIRROR of budflow-skunk
 * PageRenderer's (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.2, §13.6):
 *
 * - every element below knows the current path (from the router, so a
 *   client-side navigation re-marks the current page) and the page data;
 * - the page data and the page runtime are inlined at the end, so a cached
 *   page's states are corrected as it loads, before hydration;
 * - after a client-side navigation the runtime gets the new page's data.
 *
 * The runtime is the BUILT copy from Budflow (utils/live-host, synced) —
 * never edited here.
 */
export function LiveHost({
  live,
  currentPath,
  visitor,
  children,
}: {
  live?: (PageLiveData & { now?: number }) | null;
  /** The page's own path when it is not served at it (the preview route). */
  currentPath?: string;
  /**
   * Who the page is rendered for (audience rules, variants by audience). Only
   * a render made for ONE visitor sets it — the preview route, from the
   * builder's signed "View as" shopper — never a cached, shared page.
   */
  visitor?: VisitorAudience | null;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const path = currentPath ?? pathname;
  const host = useMemo<RenderHost>(
    () => ({ linksLive: true, currentPath: path, live: live ?? undefined, visitor: visitor ?? null }),
    [path, live, visitor],
  );

  useEffect(() => {
    // The inline script ran if this page was the first document. Arriving by
    // client navigation from a page without it (the home page), the script
    // React inserts never executes — so install from the source copy (the
    // same code; a no-op when already installed), then hand over the data.
    installPageRuntime(window).setData(live ?? {});
  }, [live]);

  return (
    <RenderHostContext.Provider value={host}>
      {children}
      <script
        type="application/json"
        id="pb-live"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify(live ?? {}).replace(/</g, '\\u003c') }}
      />
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: PAGE_RUNTIME_JS }} />
    </RenderHostContext.Provider>
  );
}

/**
 * Client navigation for builder links, site-wide — MIRROR of budflow-skunk
 * STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.7. Mount once, in the root layout.
 *
 * - Hands Remix's router to the page runtime, which then moves a click on a
 *   builder page link within the app (no new document) — the speed the
 *   hand-built header's <Link>s already have. Everything else stays with the
 *   browser (the runtime decides: other tabs, files, actions, disabled).
 * - Prefetches a builder link's page on hover or focus.
 * - After every navigation to a new PATH (a filter's query string is not
 *   one), focus moves to the new page's heading and its title is announced —
 *   for every in-app navigation, the header's included, which until now left
 *   a screen-reader user on a link that no longer existed.
 *
 * Returns the prefetch element to render in <body>.
 */
export function useClientNavigation(): ReactNode {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [prefetch, setPrefetch] = useState<string | null>(null);

  useEffect(() => {
    const runtime = installPageRuntime(window);
    runtime.setNavigator({ navigate: (href) => navigate(href), prefetch: setPrefetch });
    return () => runtime.setNavigator(null);
  }, [navigate]);

  // Compared with the last path seen, not "skip the first run": Strict Mode
  // runs an effect twice on mount, and a first-run flag let the second run
  // announce the page you'd only just loaded.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    installPageRuntime(window).pageChanged();
  }, [pathname]);

  return prefetch && prefetch !== pathname ? <PrefetchPageLinks page={prefetch} /> : null;
}

